import { useAppForm } from '@massilia/features'
import { setAccessToken, useReservationAccessMutation } from '@massilia/queries'
import { Button } from '@massilia/ui'
import { revalidateLogic, standardSchemaValidators } from '@tanstack/react-form'
import { useNavigate } from '@tanstack/react-router'
import { Info } from 'lucide-react'
import { useState } from 'react'

import { resolveAuthErrorMessage } from '@/lib/auth-error-message'
import { reservationLoginSchema, reservationLoginWithPasswordSchema } from '@/lib/schemas/'
import { useAuthStore } from '@/stores/auth.store'

import { AuthErrorComponent } from './auth-error-component'

interface ReservationNumberLoginComponentProps {
  onAccountExists?: (email: string) => void
}

export function ReservationNumberLoginComponent({
  onAccountExists,
}: ReservationNumberLoginComponentProps) {
  const [requiresPassword, setRequiresPassword] = useState(false)
  const [submitError, setSubmitError] = useState<null | string>(null)
  const { mutateAsync: reservationAccess } = useReservationAccessMutation()
  const { updateAuth } = useAuthStore()
  const navigate = useNavigate()

  const form = useAppForm({
    defaultValues: {
      email: '',
      lastName: '',
      password: '',
      passwordConfirmation: '',
      pnr: '',
    },
    listeners: {
      onChange: () => {
        if (submitError) {
          setSubmitError(null)
        }
      },
    },
    onSubmit: async ({ value }) => {
      try {
        const response = await reservationAccess({
          email: value.email,
          lastName: value.lastName,
          password: requiresPassword ? value.password : undefined,
          passwordConfirmation: requiresPassword ? value.passwordConfirmation : undefined,
          pnr: value.pnr,
        })

        if (response.status === 'ACCOUNT_EXISTS') {
          onAccountExists?.(response.email)
          return
        }

        if (response.status === 'PASSWORD_REQUIRED') {
          setRequiresPassword(true)
          return
        }

        setAccessToken(response.accessToken)
        updateAuth({
          isAuthenticated: true,
          user: {
            email: response.email,
            firstName: response.firstName,
            id: response.id,
            lastName: response.lastName,
          },
        })
        void navigate({ to: '/' })
      } catch (error) {
        setSubmitError(resolveAuthErrorMessage(error))
      }
    },
    validationLogic: revalidateLogic({
      mode: 'submit',
      modeAfterSubmission: 'blur',
    }),
    validators: {
      onDynamic: ({ value }) =>
        standardSchemaValidators.validate(
          { validationSource: 'form', value },
          requiresPassword ? reservationLoginWithPasswordSchema : reservationLoginSchema
        ),
    },
  })

  return (
    <div className="w-full">
      <form
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          void form.handleSubmit()
        }}
      >
        <div className="flex w-full flex-col gap-6">
          <form.AppField name="pnr">
            {(field) => (
              <field.TextField
                disabled={requiresPassword}
                label="PNR (Numéro de dossier)"
                placeholder="ex: ABC123"
              />
            )}
          </form.AppField>
          <form.AppField name="email">
            {(field) => (
              <field.TextField
                disabled={requiresPassword}
                label="Adresse email utilisée lors de la réservation"
                placeholder="email@example.com"
              />
            )}
          </form.AppField>
          <form.AppField name="lastName">
            {(field) => (
              <field.TextField
                disabled={requiresPassword}
                label="Nom de famille"
                placeholder="Nom de famille"
              />
            )}
          </form.AppField>
          {requiresPassword && (
            <>
              <div className="border-brand-300 bg-brand-50 rounded-md border p-4">
                <div className="mb-2 flex items-center gap-2">
                  <Info className="text-brand-700 h-4 w-4" />
                  <p className="text-brand-800 text-sm font-semibold">Dossier trouvé</p>
                </div>
                <p className="text-brand-800 text-sm">
                  Nous avons retrouvé votre dossier. Veuillez choisir un mot de passe pour créer
                  votre compte et accéder à votre espace client.
                </p>
              </div>
              <form.AppField name="password">
                {(field) => (
                  <field.TextField
                    label="Mot de passe"
                    placeholder="Mot de passe"
                    type="password"
                  />
                )}
              </form.AppField>
              <form.AppField name="passwordConfirmation">
                {(field) => (
                  <field.TextField
                    label="Confirmation du mot de passe"
                    placeholder="Confirmez votre mot de passe"
                    type="password"
                  />
                )}
              </form.AppField>
            </>
          )}
          {submitError && <AuthErrorComponent message={submitError} />}
          <form.Subscribe selector={(state) => state.isSubmitting}>
            {(isSubmitting) => (
              <Button
                className="bg-brand-600 hover:bg-brand-800 px-8 py-2 text-white"
                disabled={isSubmitting}
                type="submit"
              >
                {isSubmitting
                  ? 'Connexion en cours...'
                  : requiresPassword
                    ? 'Créer mon compte'
                    : 'Continuer'}
              </Button>
            )}
          </form.Subscribe>
        </div>
      </form>
    </div>
  )
}
